import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { exercisesApi, injectsApi, responsesApi, timelineApi, usersApi, objectivesApi, evaluationsApi, uploadsApi, exerciseTabsApi, exerciseMapApi, phoneContactsApi, mediaPortalApi, messagesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import DocumentPreview from '../../components/ems/DocumentPreview';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import ExerciseTimeline from '../../components/ems/ExerciseTimeline';
import { TimezoneDisplay, TimezoneTime } from '../../components/shared/TimezoneIndicator';
import { formatInTimezone } from '../../lib/timezone';
import {
  ArrowLeft,
  PlayCircle,
  PauseCircle,
  StopCircle,
  Target,
  Activity,
  Clock,
  Users,
  MessageSquare,
  FileText,
  ChevronRight,
  ChevronLeft,
  Plus,
  Send,
  Download,
  CheckCircle2,
  Flag,
  Phone,
  Map,
  Trash2,
  Pencil,
  Upload,
  X,
  FolderOpen,
  Radio,
  Database,
  Pin,
  PinOff,
  Grid3X3,
  List,
  Eye,
  Maximize2,
  ClipboardCheck,
} from 'lucide-react';
import ExerciseMap from '../../components/ems/ExerciseMap';
import InjectDatabasePicker from '../../components/ems/InjectDatabasePicker';
import EmbeddedMediaPortal from '../../components/ems/EmbeddedMediaPortal';
import {
  ExerciseHeader,
  OverviewTab,
  InjectsTab,
  ObjectivesTab,
  ResponsesTab,
  LogsResponsesTab,
  PhoneTab,
  ActivityPanel,
  ExerciseDialogs,
  EvaluationTab,
} from '../../components/ems/exercise-detail';

export default function ExerciseDetailPage() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { user, isController, hasRole, isObserver } = useAuth();

  const canManageExercise = isController();
  const isAdmin = hasRole(['super_admin']);
  const { joinExercise, leaveExercise } = useWebSocket();
  
  const [exercise, setExercise] = useState(null);
  const [injects, setInjects] = useState([]);
  const [responses, setResponses] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [users, setUsers] = useState([]);
  const [objectives, setObjectives] = useState([]);

  const [objectiveViewOpen, setObjectiveViewOpen] = useState(false);
  const [viewObjective, setViewObjective] = useState(null);
  const [objectiveNotesOpen, setObjectiveNotesOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [objectiveCreateOpen, setObjectiveCreateOpen] = useState(false);
  const [creatingObjective, setCreatingObjective] = useState(false);
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);
  const [newObjective, setNewObjective] = useState({
    objective_code: '',
    objective_text: '',
  });

  const [injectViewOpen, setInjectViewOpen] = useState(false);
  const [viewInject, setViewInject] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  
  // Controller (Simcell) assignment
  const [controllers, setControllers] = useState([]);
  const [assignControllersOpen, setAssignControllersOpen] = useState(false);
  const [selectedControllerIds, setSelectedControllerIds] = useState([]);
  
  // Observer assignment
  const [observers, setObservers] = useState([]);
  const [assignObserversOpen, setAssignObserversOpen] = useState(false);
  const [selectedObserverIds, setSelectedObserverIds] = useState([]);

  // Custom Tabs state
  const [customTabs, setCustomTabs] = useState([]);
  const [mapCenter, setMapCenter] = useState(null); // [lat, lon] for weather widget
  const [newTabName, setNewTabName] = useState('');
  const [addingTab, setAddingTab] = useState(false);
  const [renamingTab, setRenamingTab] = useState(null); // tab_id
  const [renameValue, setRenameValue] = useState('');
  const [uploadingFile, setUploadingFile] = useState(null); // tab_id
  const fileInputRefs = useRef({});
  const [tabViewMode, setTabViewMode] = useState({}); // { tab_id: 'list' | 'gallery' }
  const [lightboxFile, setLightboxFile] = useState(null); // file object for fullscreen view
  const [previewFile, setPreviewFile] = useState(null); // file for right-side preview panel
  const [showPhoneSystem, setShowPhoneSystem] = useState(false);
  
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [injectDialogOpen, setInjectDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedInject, setSelectedInject] = useState(null);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  
  // Form states
  const [injectForm, setInjectForm] = useState({
    inject_number: '',
    title: '',
    content: '',
    inject_time: '',
    method: '',
    dependancy: '',
    recipient: '',
    specific_function: '',
    objective: '',
    reference: '',
    injected_by: '',
    supplemental_information: '',
    key_outcomes: '',
    target_roles: [],
    target_users: [],
    priority: 'medium'
  });
  const [responseText, setResponseText] = useState('');
  const [attachments, setAttachments] = useState([]);

  // Media Portal Bridge state
  const [mpBridgeStatus, setMpBridgeStatus] = useState(null);
  const [publishingInject, setPublishingInject] = useState(null);
  
  // Inject database picker state
  const [injectDbPickerOpen, setInjectDbPickerOpen] = useState(false);
  const [editingInjectId, setEditingInjectId] = useState(null);
  const [mpPublishConfirmOpen, setMpPublishConfirmOpen] = useState(false);
  const [mpPublishInjectId, setMpPublishInjectId] = useState(null);
  const [mpPublishPlatform, setMpPublishPlatform] = useState('');

  // Inject table filters (from Control Room layout)
  const [searchFilter, setSearchFilter] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  
  // Activity panel note states
  const [noteInjectId, setNoteInjectId] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ type: 'General', objectiveMet: 'No', message: '', attachment: null });

  // Messages panel state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [msgCompose, setMsgCompose] = useState({ recipient_type: 'broadcast', subject: '', content: '', priority: 'normal', recipient_id: '', recipient_role: '' });
  const [msgSending, setMsgSending] = useState(false);
  const [msgViewItem, setMsgViewItem] = useState(null);
  const [msgReplyText, setMsgReplyText] = useState('');
  const [exerciseUsers, setExerciseUsers] = useState([]);

  // CSV import state
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvFileRef = useRef(null);

  useEffect(() => {
    loadData();
    joinExercise(exerciseId);
    
    const handleWsMessage = (event) => {
      const data = event.detail;
      if (data.type === 'inject_triggered' || data.type === 'response_submitted') {
        loadData();
      }
      if (data.type === 'exercise_control' || data.type === 'exercise_status_change') {
        loadExercise();
      }
      // V14.5 BUG 3 — inject status changes (draft/ongoing/completed) propagate live
      // to every page (Observer / Simcell / Control Room / participant)
      if (data.type === 'inject_updated' || data.type === 'inject_response') {
        loadInjects();
      }
      // V14.5 BUG 2 — exercise edit (name/timezone/company/time) propagates live
      if (data.type === 'exercise_updated') {
        loadExercise();
      }
      if (data.type === 'evaluation_created' || data.type === 'evaluation_updated' || data.type === 'evaluation_deleted') {
        window.dispatchEvent(new CustomEvent('evaluations:reload'));
      }
      if (data.type === 'objective_created' || data.type === 'objective_updated' || data.type === 'objective_deleted' ||
          data.type === 'kpi_created' || data.type === 'kpi_deleted' || data.type === 'kpis_imported' || data.type === 'objective_note_created') {
        window.dispatchEvent(new CustomEvent('objectives:reload'));
      }
      if (data.type === 'scenario_timeline_uploaded' || data.type === 'scenario_timeline_item_updated' || data.type === 'scenario_timeline_deleted') {
        window.dispatchEvent(new CustomEvent('scenario_timeline:reload'));
      }
      if (data.type?.startsWith('exercise_tab_')) {
        window.dispatchEvent(new CustomEvent('exercise_tabs:reload'));
      }
      if (data.type?.startsWith('phone_contact_')) {
        window.dispatchEvent(new CustomEvent('phone_contacts:reload'));
      }
      if (data.type?.startsWith('pbx_')) {
        window.dispatchEvent(new CustomEvent('pbx:reload'));
      }
    };
    
    window.addEventListener('ws-message', handleWsMessage);
    
    return () => {
      leaveExercise(exerciseId);
      window.removeEventListener('ws-message', handleWsMessage);
    };
    // eslint-disable-next-line
  }, [exerciseId]);

  const loadData = async () => {
    try {
      await Promise.all([
        loadExercise(),
        loadInjects(),
        loadResponses(),
        loadTimeline(),
        loadUsers(),
        loadParticipants(),
        loadControllers(),
        loadObservers(),
        loadObjectives(),
        loadCustomTabs(),
        loadMpBridgeStatus(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadExercise = async () => {
    const data = await exercisesApi.getOne(exerciseId);
    setExercise(data);
    // Load map center for weather widget
    try {
      const mapInfo = await exerciseMapApi.getMap(exerciseId);
      if (mapInfo?.center) setMapCenter(mapInfo.center);
    } catch { /* no map data yet */ }
  };

  const loadInjects = async () => {
    const data = await injectsApi.getAll(exerciseId);
    setInjects(data);
  };

  const loadResponses = async () => {
    try {
      const data = await responsesApi.getAll({
        exerciseId,
      });
      setResponses(data);
    } catch (error) {
      // Responses might fail for some users
    }
  };

  const loadTimeline = async () => {
    const data = await timelineApi.get(exerciseId);
    setTimeline(data);
  };

  const loadUsers = async () => {
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (e) {
      // Users API might fail for non-admin users
    }
  };

  const loadObjectives = async () => {
    try {
      const data = await objectivesApi.getAll(exerciseId);
      setObjectives(data);
    } catch (e) {
      // keep silent; objectives optional
    }
  };

  // V14.9 — listen for KPI-import completion in child tab so the objectives
  // list refreshes immediately (and the badge counts update).
  useEffect(() => {
    const handler = () => { loadObjectives(); };
    window.addEventListener('objectives:reload', handler);
    return () => window.removeEventListener('objectives:reload', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId]);

  const loadCustomTabs = async () => {
    try {
      const data = await exerciseTabsApi.getTabs(exerciseId);
      setCustomTabs(data || []);
    } catch (e) {
      // observers/participants won't have access; ignore silently
    }
  };

  const loadMpBridgeStatus = async () => {
    try {
      if (isController()) {
        const status = await mediaPortalApi.getBridgeStatus(exerciseId);
        setMpBridgeStatus(status);
      }
    } catch {
      // Not linked or no access
    }
  };

  const handlePublishToMediaPortal = async (injectId, platform) => {
    setPublishingInject(injectId);
    try {
      const config = platform ? { platform } : {};
      const result = await mediaPortalApi.publishInject(injectId, config);
      toast.success(`Published to Media Portal (${result.platform || result.status})`);
      loadMpBridgeStatus();
      loadInjects();
    } catch (err) {
      toast.error(err.message || 'Failed to publish to Media Portal');
    } finally {
      setPublishingInject(null);
    }
  };

  // === Messages ===
  const loadMessages = async () => {
    setMessagesLoading(true);
    try {
      const [data, usersList] = await Promise.all([
        messagesApi.getAll(exerciseId),
        usersApi.getAll(),
      ]);
      setMessages(data);
      setExerciseUsers(usersList);
    } catch { /* ignore */ }
    finally { setMessagesLoading(false); }
  };

  const handleSendMessage = async () => {
    if (!msgCompose.subject.trim() || !msgCompose.content.trim()) { toast.error('Subject and content required'); return; }
    setMsgSending(true);
    try {
      const payload = {
        exercise_id: exerciseId,
        subject: msgCompose.subject,
        content: msgCompose.content,
        priority: msgCompose.priority,
        is_group: msgCompose.recipient_type !== 'individual',
      };
      if (msgCompose.recipient_type === 'individual' && msgCompose.recipient_id) {
        payload.recipient_id = msgCompose.recipient_id;
      } else if (msgCompose.recipient_type === 'role' && msgCompose.recipient_role) {
        payload.recipient_role = msgCompose.recipient_role;
      }
      await messagesApi.send(payload);
      toast.success('Message sent');
      setMsgCompose({ recipient_type: 'broadcast', subject: '', content: '', priority: 'normal', recipient_id: '', recipient_role: '' });
      loadMessages();
    } catch (err) { toast.error(err.message || 'Failed to send'); }
    finally { setMsgSending(false); }
  };

  const handleReplyMessage = async (messageId) => {
    if (!msgReplyText.trim()) return;
    try {
      await messagesApi.reply(messageId, msgReplyText);
      toast.success('Reply sent');
      setMsgReplyText('');
      setMsgViewItem(null);
      loadMessages();
    } catch (err) { toast.error(err.message || 'Failed to reply'); }
  };

  // === CSV Import ===
  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    try {
      const result = await injectsApi.bulkImport(exerciseId, file);
      const count = result.injects_created ?? result.created ?? 0;
      toast.success(`Imported ${count} injects${result.objectives_created ? ` and ${result.objectives_created} objectives` : ''}`);
      if (result.errors?.length > 0) toast.warning(`${result.errors.length} rows had errors`);
      setCsvImportOpen(false);
      loadInjects();
    } catch (err) { toast.error(err.message || 'Import failed'); }
    finally {
      setCsvImporting(false);
      if (csvFileRef.current) csvFileRef.current.value = '';
    }
  };

  const handleCreateTab = async () => {
    if (!newTabName.trim()) return;
    try {
      const tab = await exerciseTabsApi.createTab(exerciseId, newTabName.trim());
      setCustomTabs(prev => [...prev, tab]);
      setNewTabName('');
      setAddingTab(false);
      toast.success(`Tab "${tab.name}" created`);
    } catch (e) {
      toast.error('Failed to create tab');
    }
  };

  const handleRenameTab = async (tabId) => {
    if (!renameValue.trim()) return;
    try {
      const updated = await exerciseTabsApi.updateTab(exerciseId, tabId, { name: renameValue.trim() });
      setCustomTabs(prev => prev.map(t => t.tab_id === tabId ? updated : t));
      setRenamingTab(null);
      toast.success('Tab renamed');
    } catch {
      toast.error('Failed to rename tab');
    }
  };

  const handleDeleteTab = async (tabId, tabName) => {
    if (!window.confirm(`Delete tab "${tabName}" and all its files?`)) return;
    try {
      await exerciseTabsApi.deleteTab(exerciseId, tabId);
      setCustomTabs(prev => prev.filter(t => t.tab_id !== tabId));
      toast.success('Tab deleted');
    } catch {
      toast.error('Failed to delete tab');
    }
  };

  const handleTabFileUpload = async (tabId, file) => {
    setUploadingFile(tabId);
    try {
      const fileEntry = await exerciseTabsApi.uploadFile(exerciseId, tabId, file);
      setCustomTabs(prev => prev.map(t =>
        t.tab_id === tabId ? { ...t, files: [...(t.files || []), fileEntry] } : t
      ));
      toast.success(`"${file.name}" uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleTabFileDelete = async (tabId, fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    try {
      await exerciseTabsApi.deleteFile(exerciseId, tabId, fileId);
      setCustomTabs(prev => prev.map(t =>
        t.tab_id === tabId ? { ...t, files: t.files.filter(f => f.file_id !== fileId) } : t
      ));
      toast.success('File deleted');
    } catch {
      toast.error('Failed to delete file');
    }
  };

  const handleTogglePin = async (tabId, fileId, currentPinned) => {
    try {
      const updated = await exerciseTabsApi.togglePin(exerciseId, tabId, fileId, !currentPinned);
      setCustomTabs(prev => prev.map(t => t.tab_id === tabId ? updated : t));
      toast.success(currentPinned ? 'File unpinned' : 'File pinned to top');
    } catch {
      toast.error('Failed to update pin status');
    }
  };

  const isImageFile = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const getFileUrl = (fileId) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fileId}?token=${encodeURIComponent(localStorage.getItem('ems_token') || '')}`;

  const sortFilesWithPinned = (files) => {
    if (!files) return [];
    const pinned = files.filter(f => f.pinned);
    const unpinned = files.filter(f => !f.pinned);
    return [...pinned, ...unpinned];
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return '📄';
    if (['ppt', 'pptx'].includes(ext)) return '📊';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📈';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadParticipants = async () => {
    if (!canManageExercise) return;
    try {
      const data = await usersApi.getParticipants();
      setParticipants(data);
    } catch (e) {
      // ignore
    }
  };

  const loadControllers = async () => {
    if (!isAdmin) return;
    try {
      // Get all simcell users
      const data = await usersApi.getAll();
      const simcelUsers = data.filter(u => u.roles?.includes('simcel') && !u.roles?.includes('super_admin'));
      setControllers(simcelUsers);
    } catch (e) {
      // ignore
    }
  };

  const loadObservers = async () => {
    if (!isAdmin) return;
    try {
      const data = await usersApi.getAll();
      const observerUsers = data.filter(u => u.roles?.includes('observer') && !u.roles?.includes('super_admin'));
      setObservers(observerUsers);
    } catch (e) {
      // ignore
    }
  };

  const openAssignDialog = () => {
    setSelectedParticipantIds(exercise?.assigned_users || []);
    setAssignDialogOpen(true);
  };

  const openAssignControllersDialog = () => {
    setSelectedControllerIds(exercise?.assigned_controllers || []);
    setAssignControllersOpen(true);
  };

  const openAssignObserversDialog = () => {
    setSelectedObserverIds(exercise?.assigned_observers || []);
    setAssignObserversOpen(true);
  };

  const toggleParticipant = (userId) => {
    setSelectedParticipantIds((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId);
      return [...prev, userId];
    });
  };

  const toggleController = (userId) => {
    setSelectedControllerIds((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId);
      return [...prev, userId];
    });
  };

  const toggleObserver = (userId) => {
    setSelectedObserverIds((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId);
      return [...prev, userId];
    });
  };

  const handleSaveParticipants = async () => {
    try {
      await exercisesApi.update(exerciseId, { assigned_users: selectedParticipantIds });
      toast.success('Participants updated');
      setAssignDialogOpen(false);
      await loadExercise();
    } catch (error) {
      toast.error(error.message || 'Failed to update participants');
    }
  };

  const handleSaveControllers = async () => {
    try {
      await exercisesApi.update(exerciseId, { assigned_controllers: selectedControllerIds });
      toast.success('Controllers updated');
      setAssignControllersOpen(false);
      await loadExercise();
    } catch (error) {
      toast.error(error.message || 'Failed to update controllers');
    }
  };

  const handleSaveObservers = async () => {
    try {
      await exercisesApi.update(exerciseId, { assigned_observers: selectedObserverIds });
      toast.success('Observers updated');
      setAssignObserversOpen(false);
      await loadExercise();
    } catch (error) {
      toast.error(error.message || 'Failed to update observers');
    }
  };

  const handleControlAction = async (action) => {
    try {
      await exercisesApi.control(exerciseId, action);
      toast.success(`Exercise ${action}ed successfully`);
      loadExercise();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || `Failed to ${action} exercise`);
    }
  };

  const buildInjectMetadata = (form) => {
    const raw = {
      'Inject ID': form.inject_number,
      'Time of Inject (UTC)': form.inject_time,
      'Inject Time': form.inject_time,
      Method: form.method,
      Dependancy: form.dependancy,
      Recipient: form.recipient,
      'Specific Function': form.specific_function,
      Objective: form.objective,
      'Objective ID': form.reference,
      'Inject Information': form.content,
      'Injected By': form.injected_by,
      'Supplemental Information': form.supplemental_information,
      'Key Outcomes': form.key_outcomes,
    };
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v));
  };

  const handleCreateInject = async (e) => {
    e.preventDefault();
    try {
      await injectsApi.create({
        ...injectForm,
        exercise_id: exerciseId,
        metadata: buildInjectMetadata(injectForm),
      });
      toast.success('Inject created');
      setInjectDialogOpen(false);
      resetInjectForm();
      loadInjects();
    } catch (error) {
      toast.error(error.message || 'Failed to create inject');
    }
  };

  const handleCreateObjective = async (e) => {
    e.preventDefault();
    if (!newObjective.objective_code.trim()) {
      toast.error('Objective code is required');
      return;
    }

    try {
      setCreatingObjective(true);
      if (editingObjectiveId) {
        await objectivesApi.update(editingObjectiveId, {
          objective_code: newObjective.objective_code,
          objective_text: newObjective.objective_text,
        });
        toast.success('Objective updated');
      } else {
        await objectivesApi.create({
          exercise_id: exerciseId,
          objective_code: newObjective.objective_code,
          objective_text: newObjective.objective_text,
        });
        toast.success('Objective created');
      }
      setObjectiveCreateOpen(false);
      setEditingObjectiveId(null);
      setNewObjective({ objective_code: '', objective_text: '' });
      loadObjectives();
    } catch (error) {
      toast.error(error.message || 'Failed to save objective');
    } finally {
      setCreatingObjective(false);
    }
  };

  const openEditObjective = (obj) => {
    setEditingObjectiveId(obj.objective_id);
    setNewObjective({
      objective_code: obj.objective_code || '',
      objective_text: obj.objective_text || '',
    });
    setObjectiveCreateOpen(true);
  };

  const handleTriggerInject = async (injectId) => {
    try {
      await injectsApi.trigger(injectId);
      toast.success('Inject played');
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to play inject');
    }
  };

  const handleCompleteInject = async (injectId) => {
    try {
      await injectsApi.complete(injectId);
      toast.success('Inject completed');
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to complete inject');
    }
  };

  const handleChangeInjectStatus = async (injectId, newStatus) => {
    try {
      await injectsApi.update(injectId, { status: newStatus });
      toast.success(`Inject status changed to ${newStatus}`);
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to change inject status');
    }
  };

  // V14.5 BUG 1 — remove an inject from this exercise (delete from db.injects)
  const handleDeleteInject = async (injectId) => {
    try {
      await injectsApi.delete(injectId);
      toast.success('Inject removed from exercise');
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to remove inject');
    }
  };

  // V14.5 BUG 1 — remove an objective from this exercise
  const handleDeleteObjective = async (objectiveId) => {
    try {
      await objectivesApi.delete(objectiveId);
      toast.success('Objective removed from exercise');
      const data = await objectivesApi.getAll(exerciseId);
      setObjectives(data);
    } catch (error) {
      toast.error(error.message || 'Failed to remove objective');
    }
  };

  const openEditInject = (inject) => {
    setEditingInjectId(inject.inject_id);
    const meta = inject.metadata || {};
    setInjectForm({
      inject_number: meta['Inject ID'] || meta.inject_id || inject.inject_number || inject.inject_id || '',
      title: inject.title || '',
      content: inject.content || '',
      inject_time: meta['Time of Inject (UTC)'] || meta['Inject Time'] || '',
      method: meta['Method'] || '',
      dependancy: meta['Dependancy'] || '',
      recipient: meta['Recipient'] || '',
      specific_function: meta['Specific Function'] || '',
      objective: meta['Objective'] || '',
      reference: meta['Objective ID'] || '',
      injected_by: meta['Injected By'] || '',
      supplemental_information: meta['Supplemental Information'] || '',
      key_outcomes: meta['Key Outcomes'] || '',
      target_roles: inject.target_roles || [],
      target_users: inject.target_users || [],
      priority: inject.priority || 'medium',
    });
    setInjectDialogOpen(true);
  };

  const handleUpdateInject = async (e) => {
    e.preventDefault();
    try {
      await injectsApi.update(editingInjectId, {
        ...injectForm,
        metadata: buildInjectMetadata(injectForm),
      });
      toast.success('Inject updated');
      setInjectDialogOpen(false);
      setEditingInjectId(null);
      resetInjectForm();
      loadInjects();
    } catch (error) {
      toast.error(error.message || 'Failed to update inject');
    }
  };

  const resetInjectForm = () => {
    setInjectForm({
      inject_number: '',
      title: '', content: '', inject_time: '', method: '', dependancy: '',
      recipient: '', specific_function: '', objective: '', reference: '',
      injected_by: '', supplemental_information: '', key_outcomes: '',
      target_roles: [], target_users: [], priority: 'medium',
    });
  };

  // Inject table helpers (ported from Control Room)
  const getScheduledTime = (inject) => {
    const meta = inject?.metadata || {};
    return meta['Time of Inject (UTC)'] || meta['Inject Time'] || meta['Inject Time (UTC)'] || meta.time_of_inject_utc || meta.inject_time || meta.time_of_inject || null;
  };
  const getRecipientLabel = (inject) => {
    const meta = inject?.metadata || {};
    return meta.recipient || meta.Recipient || meta['Recipient'] || meta['Inject Category'] || meta.inject_category || inject?.recipient || inject?.category || '-';
  };
  const getInjectIdLabel = (inject) => {
    const meta = inject?.metadata || {};
    return meta['Inject ID'] || meta.inject_id || inject?.inject_number || inject?.inject_id || '-';
  };
  const getDisplayTime = (inject) => {
    const scheduled = getScheduledTime(inject);
    if (scheduled) return String(scheduled);
    if (inject?.triggered_at) return new Date(inject.triggered_at).toLocaleTimeString();
    if (inject?.created_at) return new Date(inject.created_at).toLocaleTimeString();
    return '-';
  };
  const getScheduledSortValue = (inject) => {
    const scheduled = getScheduledTime(inject);
    if (!scheduled) return null;
    const cleaned = String(scheduled).replace(/(st|nd|rd|th)/g, '').replace('@', ' ').trim();
    const parsed = Date.parse(cleaned);
    if (!Number.isNaN(parsed)) return parsed;
    return cleaned.toLowerCase();
  };

  const filteredInjects = injects
    .filter(inject => {
      if (searchFilter && !inject.title?.toLowerCase().includes(searchFilter.toLowerCase()) &&
          !inject.content?.toLowerCase().includes(searchFilter.toLowerCase())) return false;
      const rv = getRecipientLabel(inject);
      if (recipientFilter !== 'All' && rv !== recipientFilter) return false;
      // Normalize pending to draft for filtering
      const normalizedStatus = inject.status === 'pending' ? 'draft' : inject.status;
      if (statusFilter !== 'all' && normalizedStatus !== statusFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortOrder === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortOrder === 'priority') {
        const po = { high: 0, medium: 1, low: 2 };
        return (po[a.priority] || 1) - (po[b.priority] || 1);
      }
      if (sortOrder === 'inject_time') {
        const av = getScheduledSortValue(a), bv = getScheduledSortValue(b);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        if (typeof av === 'string' || typeof bv === 'string') return String(av).localeCompare(String(bv));
        return av - bv;
      }
      return 0;
    });

  const sentInjects = injects.filter(i => i.status !== 'draft');

  const handleAddNote = async () => {
    if (!noteForm.message.trim() || !noteInjectId) return;
    try {
      let uploadedIds = [];
      if (noteForm.attachment) {
        const res = await uploadsApi.upload(noteForm.attachment);
        uploadedIds.push(res.file_id);
      }
      await responsesApi.create({
        inject_id: noteInjectId,
        response_text: noteForm.message,
        participant_id: user?.user_id,
        note_type: noteForm.type,
        objective_met: noteForm.objectiveMet,
        attachments: uploadedIds,
      });
      toast.success('Note saved');
      setNoteForm({ type: 'General', objectiveMet: 'No', message: '', attachment: null });
      loadResponses();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to save note');
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!selectedInject) return;

    try {
      const uploaded = [];
      for (const f of attachments) {
        // Basic boundary: 25MB
        if (f.size > 25 * 1024 * 1024) {
          throw new Error(`File too large: ${f.name}`);
        }
        const res = await uploadsApi.upload(f);
        uploaded.push(res.file_id);
      }

      await responsesApi.create({
        inject_id: selectedInject.inject_id,
        response_text: responseText,
        participant_id: user?.user_id,
        attachments: uploaded,
      });

      toast.success('Note sent');
      setResponseText('');
      setAttachments([]);
      loadResponses();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to send note');
    }
  };

  const openResponseDialog = async (inject) => {
    setSelectedInject(inject);
    setResponseDialogOpen(true);
    setResponseText('');
    setAttachments([]);
  };

  const handleSubmitObjectiveNote = async (e) => {
    e.preventDefault();
    if (!responseText.trim() || !selectedObjective) return;
    try {
      let uploadedAttachments = [];
      for (const file of attachments) {
        const uploaded = await uploadsApi.upload(file);
        uploadedAttachments.push(uploaded.file_id);
      }
      await responsesApi.create({
        exercise_id: exerciseId,
        objective_id: selectedObjective.objective_id,
        response_text: responseText,
        attachments: uploadedAttachments,
      });
      setResponseText('');
      setAttachments([]);
      loadResponses();
    } catch (err) {
      toast.error('Failed to submit note');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      live: 'status-live',
      scheduled: 'status-scheduled',
      paused: 'status-paused',
      completed: 'status-completed',
      draft: 'status-draft'
    };
    return classes[status] || 'status-draft';
  };

  const getPriorityClass = (priority) => {
    const classes = {
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  if (loading) {
    return (
      <DashboardLayout title="Exercise Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!exercise) {
    return (
      <DashboardLayout title="Exercise Not Found">
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Exercise not found</p>
            <Button asChild className="mt-4">
              <Link to="/exercises">Back to Exercises</Link>
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={exercise.name}>
      <div className="space-y-6">
        {/* Header */}
        <ExerciseHeader
          exercise={exercise}
          exerciseId={exerciseId}
          navigate={navigate}
          isController={isController}
          isObserver={isObserver}
          canManageExercise={canManageExercise}
          handleControlAction={handleControlAction}
          setEndConfirmOpen={setEndConfirmOpen}
          setStatusChangeOpen={setStatusChangeOpen}
          getStatusBadgeClass={getStatusBadgeClass}
        />

        {/* Main Layout — 2-column: Tabs content + Activity panel */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
          {/* Left: Tabs Content */}
          <div className="min-w-0">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 h-auto" data-testid="exercise-tabs-list">
            <TabsTrigger value="overview" data-testid="exercise-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="objectives" data-testid="exercise-tab-objectives">Objectives ({objectives.length})</TabsTrigger>
            <TabsTrigger value="evaluation" data-testid="exercise-tab-evaluation">
              <ClipboardCheck className="w-3 h-3 mr-1" />
              Evaluation
            </TabsTrigger>
            <TabsTrigger value="injects" data-testid="exercise-tab-injects">
              Injects ({injects.length})
            </TabsTrigger>
            {(isController() || isObserver()) && (
              <TabsTrigger value="media-portal" data-testid="exercise-tab-media-portal">
                <Radio className="w-3 h-3 mr-1" />
                Media Portal
              </TabsTrigger>
            )}
            <TabsTrigger value="timeline" data-testid="exercise-tab-timeline">Timeline</TabsTrigger>
            {isController() && (
              <TabsTrigger value="responses" data-testid="exercise-tab-responses">
                Logs/Responses ({responses.length})
              </TabsTrigger>
            )}
            {!isController() && (
              <TabsTrigger value="responses" data-testid="exercise-tab-responses">
                Responses ({responses.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="phone" data-testid="exercise-tab-phone">
              <Phone className="w-3 h-3 mr-1" />
              Phone
            </TabsTrigger>
            <TabsTrigger value="exercise-map" data-testid="exercise-tab-map">
              <Map className="w-3 h-3 mr-1" />
              Exercise Map
            </TabsTrigger>
            {customTabs.map(tab => (
              <TabsTrigger key={tab.tab_id} value={`custom-${tab.tab_id}`} data-testid={`exercise-tab-custom-${tab.tab_id}`}>
                <FolderOpen className="w-3 h-3 mr-1" />
                {tab.name}
              </TabsTrigger>
            ))}
            {(canManageExercise || isAdmin) && (
              <button
                type="button"
                onClick={() => setAddingTab(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                data-testid="add-custom-tab-btn"
              >
                <Plus className="w-3 h-3" /> Add Tab
              </button>
            )}
          </TabsList>

          {addingTab && (
            <div className="flex items-center gap-2 mt-2" data-testid="add-tab-form">
              <Input
                autoFocus
                placeholder="Tab name (e.g. Briefing Pack)"
                value={newTabName}
                onChange={e => setNewTabName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateTab(); if (e.key === 'Escape') { setAddingTab(false); setNewTabName(''); } }}
                className="max-w-xs h-8 text-sm"
                data-testid="new-tab-name-input"
              />
              <Button size="sm" onClick={handleCreateTab} disabled={!newTabName.trim()} data-testid="create-tab-confirm-btn">Create</Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddingTab(false); setNewTabName(''); }}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}

          {/* Overview Tab */}
          <TabsContent value="overview">
            <OverviewTab
              exercise={exercise} injects={injects} responses={responses} timeline={timeline} objectives={objectives}
              isAdmin={isAdmin} isObserver={isObserver} canManageExercise={canManageExercise}
              openAssignDialog={openAssignDialog} openAssignControllersDialog={openAssignControllersDialog} openAssignObserversDialog={openAssignObserversDialog}
              getStatusBadgeClass={getStatusBadgeClass}
            />
          </TabsContent>

          {/* Injects Tab */}
          <TabsContent value="injects">
            <InjectsTab
              filteredInjects={filteredInjects} injects={injects} responses={responses} exerciseId={exerciseId}
              searchFilter={searchFilter} setSearchFilter={setSearchFilter}
              recipientFilter={recipientFilter} setRecipientFilter={setRecipientFilter}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              sortOrder={sortOrder} setSortOrder={setSortOrder}
              isController={isController} isObserver={isObserver}
              mpBridgeStatus={mpBridgeStatus} publishingInject={publishingInject}
              setCsvImportOpen={setCsvImportOpen} setInjectDbPickerOpen={setInjectDbPickerOpen}
              setEditingInjectId={setEditingInjectId} resetInjectForm={resetInjectForm} setInjectDialogOpen={setInjectDialogOpen}
              handleTriggerInject={handleTriggerInject} handleCompleteInject={handleCompleteInject} handleChangeInjectStatus={handleChangeInjectStatus}
              setViewInject={setViewInject} setInjectViewOpen={setInjectViewOpen}
              openResponseDialog={openResponseDialog} openEditInject={openEditInject}
              setMpPublishInjectId={setMpPublishInjectId} setMpPublishPlatform={setMpPublishPlatform} setMpPublishConfirmOpen={setMpPublishConfirmOpen}
              getRecipientLabel={getRecipientLabel} getInjectIdLabel={getInjectIdLabel} getDisplayTime={getDisplayTime} getPriorityClass={getPriorityClass}
              handleDeleteInject={handleDeleteInject} exerciseStatus={exercise?.status}
            />
          </TabsContent>

          <TabsContent value="objectives">
            <ObjectivesTab
              objectives={objectives} isController={isController} isObserver={isObserver}
              setObjectiveCreateOpen={(v) => { if (v) setEditingObjectiveId(null); setObjectiveCreateOpen(v); }}
              setViewObjective={setViewObjective} setObjectiveViewOpen={setObjectiveViewOpen}
              handleDeleteObjective={handleDeleteObjective} exerciseStatus={exercise?.status}
              exerciseId={exerciseId}
              openEditObjective={openEditObjective}
            />
          </TabsContent>

          <TabsContent value="evaluation">
            <EvaluationTab
              exerciseId={exerciseId}
              objectives={objectives}
              isController={isController}
            />
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <ExerciseTimeline 
              exercise={exercise}
              injects={injects}
              timelineEvents={timeline}
              timezone={exercise.timezone || 'UTC'}
              showOnlyActual={!canManageExercise && !isAdmin}
            />
          </TabsContent>

          {/* Media Portal Embedded Tab */}
          <TabsContent value="media-portal">
            <EmbeddedMediaPortal exerciseId={exerciseId} exercise={exercise} />
          </TabsContent>

          <TabsContent value="responses">
            {isController() ? (
              <LogsResponsesTab
                exerciseId={exerciseId}
                exercise={exercise}
                responses={responses}
                injects={injects}
                openResponseDialog={openResponseDialog}
                getFileUrl={getFileUrl}
              />
            ) : (
              <ResponsesTab
                responses={responses} injects={injects}
                openResponseDialog={openResponseDialog} getFileUrl={getFileUrl}
              />
            )}
          </TabsContent>

          <TabsContent value="phone">
            <PhoneTab
              exerciseId={exerciseId} canManageExercise={canManageExercise}
              isAdmin={isAdmin} hasRole={hasRole}
              showPhoneSystem={showPhoneSystem} setShowPhoneSystem={setShowPhoneSystem}
            />
          </TabsContent>

          <TabsContent value="exercise-map">
            <ExerciseMap
              exerciseId={exerciseId}
              canEdit={!hasRole(['participant']) && (canManageExercise || isAdmin)}
              mapApi={exerciseMapApi}
            />
          </TabsContent>

          {/* Custom Document Tabs */}
          {customTabs.map(tab => (
            <TabsContent key={tab.tab_id} value={`custom-${tab.tab_id}`}>
              <div className="space-y-4">
                {/* Tab header with rename/delete + view toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-primary" />
                    {renamingTab === tab.tab_id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameTab(tab.tab_id); if (e.key === 'Escape') setRenamingTab(null); }}
                          className="h-7 text-sm max-w-[200px]"
                          data-testid="rename-tab-input"
                        />
                        <Button size="sm" className="h-7" onClick={() => handleRenameTab(tab.tab_id)}>Save</Button>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => setRenamingTab(null)}><X className="w-3 h-3" /></Button>
                      </div>
                    ) : (
                      <span className="font-semibold">{tab.name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* View mode toggle */}
                    <div className="flex items-center border rounded-md overflow-hidden mr-2" data-testid={`view-mode-toggle-${tab.tab_id}`}>
                      <button
                        onClick={() => setTabViewMode(prev => ({ ...prev, [tab.tab_id]: 'list' }))}
                        className={`px-2 py-1.5 flex items-center gap-1 text-xs transition-colors ${(tabViewMode[tab.tab_id] || 'list') === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
                        title="List view"
                        data-testid={`list-view-btn-${tab.tab_id}`}
                      >
                        <List className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setTabViewMode(prev => ({ ...prev, [tab.tab_id]: 'gallery' }))}
                        className={`px-2 py-1.5 flex items-center gap-1 text-xs transition-colors ${tabViewMode[tab.tab_id] === 'gallery' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
                        title="Gallery view"
                        data-testid={`gallery-view-btn-${tab.tab_id}`}
                      >
                        <Grid3X3 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {(canManageExercise || isAdmin) && (
                      <>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => { setRenamingTab(tab.tab_id); setRenameValue(tab.name); }}
                          data-testid={`rename-tab-btn-${tab.tab_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteTab(tab.tab_id, tab.name)}
                          data-testid={`delete-tab-btn-${tab.tab_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* File display — split layout with preview panel */}
                <div className={`${previewFile ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}`}>
                <div>
                  {(tab.files || []).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg border-dashed">
                      No files uploaded yet
                      {(canManageExercise || isAdmin) && <p className="text-xs mt-1">Use the Upload button below to add files</p>}
                    </div>
                  ) : (tabViewMode[tab.tab_id] || 'list') === 'gallery' ? (
                    /* ── Gallery View ── */
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={`gallery-grid-${tab.tab_id}`}>
                      {sortFilesWithPinned(tab.files).map(file => (
                        <div
                          key={file.file_id}
                          className={`group relative rounded-lg border overflow-hidden bg-card hover:shadow-lg transition-all ${file.pinned ? 'ring-2 ring-primary/40' : ''}`}
                          data-testid={`gallery-item-${file.file_id}`}
                        >
                          {/* Pin badge */}
                          {file.pinned && (
                            <div className="absolute top-2 left-2 z-10">
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 gap-0.5 bg-primary/90 text-primary-foreground">
                                <Pin className="w-2.5 h-2.5" /> Pinned
                              </Badge>
                            </div>
                          )}

                          {/* Image preview or file icon */}
                          {isImageFile(file.filename) ? (
                            <div
                              className="aspect-square bg-muted cursor-pointer relative overflow-hidden"
                              onClick={() => setLightboxFile(file)}
                            >
                              <img
                                src={getFileUrl(file.file_id)}
                                alt={file.filename}
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                              </div>
                            </div>
                          ) : (
                            <div
                              className="aspect-square bg-muted/50 flex flex-col items-center justify-center cursor-pointer"
                              onClick={() => setPreviewFile(file)}
                            >
                              <span className="text-4xl mb-2">{getFileIcon(file.filename)}</span>
                              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                                {file.filename?.split('.').pop()}
                              </span>
                            </div>
                          )}

                          {/* File info footer */}
                          <div className="p-2 space-y-1">
                            <p className="text-xs font-medium truncate" title={file.filename}>{file.filename}</p>
                            <p className="text-[10px] text-muted-foreground">{formatFileSize(file.size)}</p>
                          </div>

                          {/* Action buttons overlay */}
                          <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {(canManageExercise || isAdmin) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleTogglePin(tab.tab_id, file.file_id, file.pinned); }}
                                className={`w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm transition-colors ${file.pinned ? 'bg-primary text-primary-foreground' : 'bg-background/80 text-muted-foreground hover:text-primary'}`}
                                title={file.pinned ? 'Unpin file' : 'Pin to top'}
                                data-testid={`pin-file-${file.file_id}`}
                              >
                                {file.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                              </button>
                            )}
                            <a
                              href={getFileUrl(file.file_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="w-7 h-7 rounded-full bg-background/80 flex items-center justify-center text-muted-foreground hover:text-primary backdrop-blur-sm transition-colors"
                              title="Download"
                              data-testid={`download-file-${file.file_id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                            {(canManageExercise || isAdmin) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleTabFileDelete(tab.tab_id, file.file_id, file.filename); }}
                                className="w-7 h-7 rounded-full bg-background/80 flex items-center justify-center text-muted-foreground hover:text-destructive backdrop-blur-sm transition-colors"
                                title="Delete"
                                data-testid={`delete-file-${file.file_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* ── List View ── */
                    <div className="space-y-2" data-testid={`list-view-${tab.tab_id}`}>
                      {sortFilesWithPinned(tab.files).map(file => (
                        <div key={file.file_id} className={`flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors cursor-pointer ${file.pinned ? 'border-primary/40 bg-primary/5' : ''} ${previewFile?.file_id === file.file_id ? 'ring-2 ring-primary' : ''}`} onClick={() => setPreviewFile(file)} data-testid={`list-item-${file.file_id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {file.pinned && <Pin className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
                            {isImageFile(file.filename) ? (
                              <img
                                src={getFileUrl(file.file_id)}
                                alt={file.filename}
                                className="w-10 h-10 rounded object-cover flex-shrink-0 cursor-pointer border"
                                onClick={() => setLightboxFile(file)}
                                loading="lazy"
                              />
                            ) : (
                              <span className="text-lg flex-shrink-0">{getFileIcon(file.filename)}</span>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{file.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.size)}
                                {file.uploaded_by_name && ` · by ${file.uploaded_by_name}`}
                                {file.uploaded_at && ` · ${new Date(file.uploaded_at).toLocaleDateString()}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                            {(canManageExercise || isAdmin) && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => handleTogglePin(tab.tab_id, file.file_id, file.pinned)}
                                title={file.pinned ? 'Unpin' : 'Pin to top'}
                                data-testid={`pin-file-${file.file_id}`}
                              >
                                {file.pinned ? <PinOff className="w-3.5 h-3.5 text-primary" /> : <Pin className="w-3.5 h-3.5" />}
                              </Button>
                            )}
                            {isImageFile(file.filename) && (
                              <Button size="sm" variant="ghost" onClick={() => setLightboxFile(file)} title="Full view" data-testid={`view-file-${file.file_id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <a
                              href={getFileUrl(file.file_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`download-file-${file.file_id}`}
                            >
                              <Button size="sm" variant="ghost">
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            </a>
                            {(canManageExercise || isAdmin) && (
                              <Button
                                size="sm" variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleTabFileDelete(tab.tab_id, file.file_id, file.filename)}
                                data-testid={`delete-file-${file.file_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Document Preview Panel (right side) */}
                {previewFile && (
                  <div className="h-[500px] lg:h-[600px]" data-testid="doc-preview-container">
                    <DocumentPreview file={previewFile} onClose={() => setPreviewFile(null)} />
                  </div>
                )}
                </div>

                {/* Upload button */}
                {(canManageExercise || isAdmin) && (
                  <div>
                    <input
                      ref={el => { fileInputRefs.current[tab.tab_id] = el; }}
                      type="file"
                      className="hidden"
                      accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp,.zip"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await handleTabFileUpload(tab.tab_id, file);
                        e.target.value = '';
                      }}
                      data-testid={`file-input-${tab.tab_id}`}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={uploadingFile === tab.tab_id}
                      onClick={() => fileInputRefs.current[tab.tab_id]?.click()}
                      data-testid={`upload-file-btn-${tab.tab_id}`}
                    >
                      <Upload className="w-3.5 h-3.5 mr-2" />
                      {uploadingFile === tab.tab_id ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}

          {/* Lightbox / Full-screen file viewer */}
          {lightboxFile && (
            <div
              className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
              onClick={() => setLightboxFile(null)}
              data-testid="lightbox-overlay"
            >
              <button
                onClick={() => setLightboxFile(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                data-testid="lightbox-close"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="absolute top-4 left-4 z-10 text-white">
                <p className="text-sm font-medium">{lightboxFile.filename}</p>
                <p className="text-xs text-white/60">{formatFileSize(lightboxFile.size)}</p>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                <a
                  href={getFileUrl(lightboxFile.file_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-2 transition-colors backdrop-blur-sm"
                  data-testid="lightbox-download"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              </div>
              {isImageFile(lightboxFile.filename) ? (
                <img
                  src={getFileUrl(lightboxFile.file_id)}
                  alt={lightboxFile.filename}
                  className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <div className="bg-card rounded-xl p-12 text-center" onClick={e => e.stopPropagation()}>
                  <span className="text-6xl mb-4 block">{getFileIcon(lightboxFile.filename)}</span>
                  <p className="text-lg font-medium">{lightboxFile.filename}</p>
                  <p className="text-sm text-muted-foreground mt-1">{formatFileSize(lightboxFile.size)}</p>
                </div>
              )}
            </div>
          )}
        </Tabs>
          </div>{/* end left column */}

          <ActivityPanel
            exerciseId={exerciseId} exercise={exercise} injects={injects} user={user}
            loadMessages={loadMessages} loadTimeline={loadTimeline} loadResponses={loadResponses}
            noteInjectId={noteInjectId} setNoteInjectId={setNoteInjectId}
            noteForm={noteForm} setNoteForm={setNoteForm} handleAddNote={handleAddNote}
            messages={messages} messagesLoading={messagesLoading}
            msgCompose={msgCompose} setMsgCompose={setMsgCompose}
            msgSending={msgSending} handleSendMessage={handleSendMessage}
            msgViewItem={msgViewItem} setMsgViewItem={setMsgViewItem}
            msgReplyText={msgReplyText} setMsgReplyText={setMsgReplyText} handleReplyMessage={handleReplyMessage}
            exerciseUsers={exerciseUsers}
            onStreamItemClick={async (item) => {
              // V14.5 BUG 5 — clicking a stream entry opens the source dialog.
              // V14.11.5 — When the inject isn't in the locally cached array
              // (e.g. it was triggered AFTER the page loaded, or by another
              // controller), fall back to fetching it directly by ID instead
              // of silently doing nothing.
              if (item.inject_id) {
                let inj = injects.find((i) => i.inject_id === item.inject_id);
                if (!inj) {
                  try { inj = await injectsApi.getOne(item.inject_id); } catch { /* noop */ }
                  if (inj) loadInjects?.();  // refresh local cache for next time
                }
                if (inj) {
                  setViewInject(inj);
                  setInjectViewOpen(true);
                  return;
                }
              }
              if (item.message_id) {
                const m = messages.find((x) => x.message_id === item.message_id);
                if (m) {
                  setMsgViewItem(m);
                  return;
                }
                // fallback: load and try again
                loadMessages?.();
                return;
              }
              if (item.objective_id) {
                const o = objectives.find((x) => x.objective_id === item.objective_id);
                if (o) {
                  setViewObjective(o);
                  setObjectiveViewOpen(true);
                }
              }
            }}
          />
        </div>{/* end grid */}
        
        <ExerciseDialogs
          injectDialogOpen={injectDialogOpen} setInjectDialogOpen={setInjectDialogOpen}
          editingInjectId={editingInjectId} setEditingInjectId={setEditingInjectId}
          injectForm={injectForm} setInjectForm={setInjectForm} resetInjectForm={resetInjectForm}
          handleCreateInject={handleCreateInject} handleUpdateInject={handleUpdateInject}
          objectiveCreateOpen={objectiveCreateOpen} setObjectiveCreateOpen={(v) => { setObjectiveCreateOpen(v); if (!v) { setEditingObjectiveId(null); setNewObjective({ objective_code: '', objective_text: '' }); } }}
          newObjective={newObjective} setNewObjective={setNewObjective}
          creatingObjective={creatingObjective} handleCreateObjective={handleCreateObjective}
          editingObjectiveId={editingObjectiveId}
          openEditObjective={openEditObjective}
          objectiveViewOpen={objectiveViewOpen} setObjectiveViewOpen={setObjectiveViewOpen} viewObjective={viewObjective}
          setSelectedObjective={setSelectedObjective} setObjectiveNotesOpen={setObjectiveNotesOpen}
          injectViewOpen={injectViewOpen} setInjectViewOpen={setInjectViewOpen} viewInject={viewInject}
          openResponseDialog={openResponseDialog} openEditInject={openEditInject} isController={isController} isObserver={isObserver}
          responseDialogOpen={responseDialogOpen} setResponseDialogOpen={setResponseDialogOpen}
          selectedInject={selectedInject} responses={responses}
          responseText={responseText} setResponseText={setResponseText}
          attachments={attachments} setAttachments={setAttachments}
          handleSubmitResponse={handleSubmitResponse} getFileUrl={getFileUrl}
          objectiveNotesOpen={objectiveNotesOpen} selectedObjective={selectedObjective}
          handleSubmitObjectiveNote={handleSubmitObjectiveNote}
          assignDialogOpen={assignDialogOpen} setAssignDialogOpen={setAssignDialogOpen}
          participants={participants} selectedParticipantIds={selectedParticipantIds}
          toggleParticipant={toggleParticipant} handleSaveParticipants={handleSaveParticipants}
          assignControllersOpen={assignControllersOpen} setAssignControllersOpen={setAssignControllersOpen}
          controllers={controllers} selectedControllerIds={selectedControllerIds}
          toggleController={toggleController} handleSaveControllers={handleSaveControllers}
          assignObserversOpen={assignObserversOpen} setAssignObserversOpen={setAssignObserversOpen}
          observers={observers} selectedObserverIds={selectedObserverIds}
          toggleObserver={toggleObserver} handleSaveObservers={handleSaveObservers}
          isAdmin={isAdmin} canManageExercise={canManageExercise}
          endConfirmOpen={endConfirmOpen} setEndConfirmOpen={setEndConfirmOpen}
          handleControlAction={handleControlAction}
          statusChangeOpen={statusChangeOpen} setStatusChangeOpen={setStatusChangeOpen}
          exercise={exercise} exerciseId={exerciseId} exercisesApi={exercisesApi}
          loadExercise={loadExercise} getStatusBadgeClass={getStatusBadgeClass}
          mpPublishConfirmOpen={mpPublishConfirmOpen} setMpPublishConfirmOpen={setMpPublishConfirmOpen}
          mpPublishInjectId={mpPublishInjectId} setMpPublishInjectId={setMpPublishInjectId}
          mpPublishPlatform={mpPublishPlatform} setMpPublishPlatform={setMpPublishPlatform}
          mpBridgeStatus={mpBridgeStatus} handlePublishToMediaPortal={handlePublishToMediaPortal}
          csvImportOpen={csvImportOpen} setCsvImportOpen={setCsvImportOpen}
          csvImporting={csvImporting} csvFileRef={csvFileRef} handleCsvImport={handleCsvImport}
          injectDbPickerOpen={injectDbPickerOpen} setInjectDbPickerOpen={setInjectDbPickerOpen}
          loadInjects={loadInjects}
        />

      </div>

      <InjectDatabasePicker
        exerciseId={exerciseId}
        open={injectDbPickerOpen}
        onOpenChange={setInjectDbPickerOpen}
        onAdded={loadInjects}
      />

    </DashboardLayout>
  );
}
